// WHAT: Status bar component displaying roadmap overview metrics and progress
// WHY:  Provides quick overview of roadmap statistics (totals, status counts, completion rate)
// DOMAIN: Admin → Roadmap → Overview
// DEPENDENCIES: useRoadmapContext hook, Card, Badge, ProgressBar components
// RISKS:
//   - NOTE: Dynamic Tailwind classes may not work with JIT compiler
//   - NOTE: Division by zero check for percentage calculation
//   - NOTE: Metrics calculated from all items - not filtered items

import {
  LucideList,
  LucideClock,
  LucideCheckCircle,
  LucideAlertTriangle,
  LucideTrendingUp,
} from "lucide-react";
import React from "react";

import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { ProgressBar } from "@/components/ui/Progress";
import { useRoadmapContext } from "@/domains/admin/roadmap/context";

/* ------------------------------------------------------------ */
/* Metric Card Component                                         */
/* ------------------------------------------------------------ */

/**
 * Props for MetricCard component
 */
interface MetricCardProperties {
  /** Optional description badge */
  description?: string;
  /** Icon to display */
  icon: React.ReactNode;
  /** Label for the metric */
  label: string;
  /** Numeric value to display */
  value: number;
  /** Visual variant for styling */
  variant: "accent" | "info" | "primary" | "warning";
}

/**
 * WHAT:
 *   Reusable metric card component for displaying statistics.
 *
 * WHY:
 *   Provides consistent styling for metric displays across the status bar.
 *
 * BEHAVIOR:
 *   - Displays icon, value, label, and optional description badge
 *   - Formats value with locale string (thousand separators)
 *   - Uses variant for color styling
 *
 * @param root0
 *
 * @param root0.label
 *
 * @param root0.value
 *
 * @param root0.icon
 *
 * @param root0.variant
 *
 * @param root0.description
 *
 * @returns {JSX.Element} Metric card component
 *
 */
/**
 * Get background color class for variant
 *
 * @param variant - Variant type
 *
 * @returns {string} Background color class
 *
 */
const getVariantBgClass = (variant: "accent" | "info" | "primary" | "warning"): string => {
  switch (variant) {
    case "primary": {
      return "bg-tracq-bright-blue/10";
    }
    case "info": {
      return "bg-blue-500/10";
    }
    case "warning": {
      return "bg-orange-500/10";
    }
    case "accent": {
      return "bg-tracq-deep-orange/10";
    }
    default: {
      return "bg-tracq-deep-orange/10";
    }
  }
};

const MetricCard: React.FC<MetricCardProperties> = ({
  description,
  icon,
  label,
  value,
  variant,
}) => (
  <Card className="text-center" padding="md" variant="default">
    <div className="flex items-center justify-between mb-3">
      <div className={`p-2 rounded-lg ${getVariantBgClass(variant)}`}>{icon}</div>
      {description ? (
        <Badge size="sm" style="subtle" variant={variant}>
          {description}
        </Badge>
      ) : null}
    </div>

    <div className="text-center">
      <div className="text-2xl font-bold text-white mb-1">{value.toLocaleString()}</div>
      <div className="text-sm text-slate-400 font-medium">{label}</div>
    </div>
  </Card>
);

/* ------------------------------------------------------------ */
/* Progress Indicator Component                                  */
/* ------------------------------------------------------------ */

/**
 * Props for ProgressIndicator component
 */
interface ProgressIndicatorProperties {
  /** Number of completed items */
  completed: number;
  /** Label for the progress indicator */
  label: string;
  /** Total number of items */
  total: number;
}

/**
 * WHAT:
 *   Component that displays completion progress with percentage and progress bar.
 *
 * WHY:
 *   Provides visual representation of roadmap completion status.
 *
 * BEHAVIOR:
 *   - Calculates percentage (completed / total * 100)
 *   - Handles division by zero (returns 0%)
 *   - Displays percentage, label, progress bar, and count
 *
 * @param root0
 *
 * @param root0.completed
 *
 * @param root0.total
 *
 * @param root0.label
 *
 * @returns {JSX.Element} Progress indicator component
 *
 */
const ProgressIndicator: React.FC<ProgressIndicatorProperties> = ({ completed, label, total }) => {
  // NOTE: Calculate percentage with division by zero protection
  const percentage = total > 0 ? (completed / total) * 100 : 0;

  return (
    <Card padding="md" variant="default">
      <div className="text-center mb-4">
        <div className="text-2xl font-bold text-white mb-1">{percentage.toFixed(1)}%</div>
        <div className="text-sm text-slate-400 font-medium">{label}</div>
      </div>

      <ProgressBar
        className="mb-2"
        max={100}
        showLabel={false}
        size="md"
        state="success"
        value={percentage}
      />

      <div className="text-xs text-slate-400 text-center">
        {completed} of {total} completed
      </div>
    </Card>
  );
};

/* ------------------------------------------------------------ */
/* Main StatusBar Component                                      */
/* ------------------------------------------------------------ */

/**
 * WHAT:
 *   Main status bar component displaying roadmap overview metrics including
 *   totals, status counts, completion rate, and high priority items.
 *
 * WHY:
 *   Provides a quick overview of roadmap health and progress at a glance.
 *
 * BEHAVIOR:
 *   - Calculates metrics from all items in context
 *   - Displays metric cards in responsive grid
 *   - Shows completion progress with progress bar
 *   - Displays status breakdown
 *
 * RISKS:
 *   - NOTE: Metrics calculated from all items - not filtered items
 *   - NOTE: Dynamic Tailwind classes may not be purged correctly
 *   - NOTE: Division by zero handled in ProgressIndicator component
 *
 * @returns {JSX.Element} Status bar component
 *
 */
export const StatusBar = () => {
  // NOTE: Get all items from context
  const { items } = useRoadmapContext();

  // NOTE: Calculate metrics from all items
  const total = items.length;
  const open = items.filter((index) => index.status === "open").length;
  const inProgress = items.filter((index) => index.status === "in_progress").length;
  const done = items.filter((index) => index.status === "done").length;
  const highPriority = items.filter((index) => index.priority === "high").length;
  const completed = done;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <LucideTrendingUp className="w-5 h-5 text-tracq-bright-blue" />
        <h3 className="text-lg font-semibold text-white">Roadmap Overview</h3>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          description="All roadmap items"
          icon={<LucideList className="w-5 h-5 text-tracq-bright-blue" />}
          label="Total Items"
          value={total}
          variant="primary"
        />

        <MetricCard
          description="Pending items"
          icon={<LucideClock className="w-5 h-5 text-blue-500" />}
          label="Open"
          value={open}
          variant="info"
        />

        <MetricCard
          description="Active development"
          icon={<LucideAlertTriangle className="w-5 h-5 text-orange-500" />}
          label="In Progress"
          value={inProgress}
          variant="warning"
        />

        <MetricCard
          description="Critical items"
          icon={<LucideCheckCircle className="w-5 h-5 text-tracq-deep-orange" />}
          label="High Priority"
          value={highPriority}
          variant="accent"
        />
      </div>

      {/* Progress Section */}
      {total > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ProgressIndicator completed={completed} label="Completion Rate" total={total} />

          <Card padding="md" variant="default">
            <div className="text-center mb-4">
              <div className="text-2xl font-bold text-white mb-1">{done}</div>
              <div className="text-sm text-slate-400 font-medium">Completed Items</div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Open</span>
                <Badge size="sm" style="subtle" variant="info">
                  {open}
                </Badge>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">In Progress</span>
                <Badge size="sm" style="subtle" variant="warning">
                  {inProgress}
                </Badge>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Done</span>
                <Badge size="sm" style="subtle" variant="success">
                  {done}
                </Badge>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
